import { RootStore } from './root.store';
import UrlParser from '../utils/url-parser';
import { getQuote, getQuotePreview } from '../api/quote';
import { makeAutoObservable, runInAction } from 'mobx';
import { ContractTypeId, CustomerStatus, EquipmentCategories, IQuote, LineItemCategory, OnboardingType } from '../domains/quote.domain';
import { ErrorCode, QuoteErrors } from '../domains/errorCode';
import { IDevice, IOnboarding, IQuoteShortInfo } from '../domains/signup.domain';
import { getFormattedPrice } from '../utils/formatter';
import { DefaultLanguage, Languages } from '../translations/i18n';

export class QuoteStore {
  root: RootStore;

  private _quoteInfo: IQuote | null = null;
  private _isLoading: boolean = false;

  constructor(root: RootStore) {
    makeAutoObservable(this);
    this.root = root;
  }

  async init(): Promise<void> {
    try {
      const tenantId = UrlParser.getTenantId();
      const quoteShortCode = UrlParser.getQuoteShortCode();
      const isPreview = UrlParser.getUrlPath() == '/quotepreview.aspx';
      if (quoteShortCode) {
        this._quoteInfo = isPreview ? await getQuotePreview(tenantId, quoteShortCode) : await getQuote(tenantId, quoteShortCode);
        if (this._quoteInfo?.customer?.language) {
          const quoteLanguage = this.getQuoteLanguage(this._quoteInfo.customer.language.toLowerCase());
          await this.root.signUpStore.changeLanguage(quoteLanguage);
        }
        this.initQuoteShortInfo();
        this.initOnboarding();
        runInAction(() => (this._isLoading = true));
        this.setUpCreditCardScript();
      } else {
        this.root.signUpStore.setErrorCode(ErrorCode.InvalidQuote);
      }
    } catch (e) {
      const responseCode = e.response?.data?.code;
      const errorCode = QuoteErrors.includes(responseCode) ? responseCode : ErrorCode.InvalidQuote;
      this.root.signUpStore.setErrorCode(errorCode);
    }
  }

  get quoteInfo(): IQuote | null {
    return this._quoteInfo;
  }

  initQuoteShortInfo() {
    const { culture, currency } = this.root.tenantStore.config;
    let info: IQuoteShortInfo | undefined = undefined;
    if (this._quoteInfo) {
      this.root.shippingInformationStore.updateInformation('shippingMethod', this._quoteInfo.shipping?.shippingOption);
      info = {
        id: this._quoteInfo.id,
        customerName: this._quoteInfo.customer?.name,
        nrc: getFormattedPrice(this._quoteInfo.totals?.nrc, culture, currency),
        mrc: getFormattedPrice(this._quoteInfo.totals?.mrc, culture, currency),
        promoMrc: getFormattedPrice(this._quoteInfo.totals?.promoMrc, culture, currency),
        upgradeMrc: getFormattedPrice(this._quoteInfo.totals?.upgradeMrc, culture, currency),
        html: this._quoteInfo.html,
        hasPromo: !!this._quoteInfo.promo?.code,
        promoDuration: this._quoteInfo.promo?.duration,
        hasNoAdditions: this._quoteInfo.hasNoAdditions,
        isUpgrade: this._quoteInfo.contractTypeId === ContractTypeId.Upgrade,
        isRenewal: this._quoteInfo.contractTypeId === ContractTypeId.Renewal,
      };
    }
    this.root.signUpStore.setQuoteShortInfo(info);
  }

  initOnboarding() {
    let onboarding: IOnboarding | undefined = undefined;

    if (this._quoteInfo) {
      onboarding = {
        type: this._quoteInfo.onboardingType,
      };
    }

    this.root.signUpStore.setOnboarding(onboarding);
  }

  get displayShippingOptions(): boolean {
    return this.root.tenantStore.config.allowShipmentOptions && !this._quoteInfo?.customer?.demoCustomer;
  }

  get isLoading(): any {
    return this._isLoading;
  }

  get isOnboardingStepAvailable(): boolean {
    return !this.hasNoAdditions;
  }

  get isDelegateStepAvailable(): boolean {
    return !!this.quoteInfo?.customer.customerDelegates.length && !this.hasNoAdditions;
  }

  get isServiceStepAvailable(): boolean {
    return (!this.isCustomerInService || this.isSelfOnboarding) && !this.hasNoAdditions && this.root.tenantStore.config?.displayServiceInfo;
  }

  get isShippingStepAvailable(): boolean {
    return this.isEquipmentOrder && !this.hasNoAdditions;
  }

  get isBillingStepAvailable(): boolean {
    return (!this.isCustomerInService || this.isSelfOnboarding) && !this.hasNoAdditions && !this.quoteInfo?.customer?.billingStatus;
  }

  get isPaymentStepAvailable(): boolean {
    const config = this.root.tenantStore.config;
    const customer = this.quoteInfo?.customer;
    const parentCustomer = this.quoteInfo?.parentCustomer;
    return (
      !(
        !config.displayPayment ||
        parentCustomer?.status === CustomerStatus.InService ||
        parentCustomer?.childrenInService ||
        parentCustomer?.alternatePayment ||
        parentCustomer?.demoCustomer ||
        customer?.status === CustomerStatus.InService ||
        customer?.demoCustomer ||
        customer?.alternatePayment
      ) && !this.hasNoAdditions
    );
  }

  get isCustomerInService(): boolean {
    return this.quoteInfo?.customer.status === CustomerStatus.InService;
  }

  get isContactOnlyPartner(): boolean {
    const quoteCustomer = this.quoteInfo?.customer;
    return (quoteCustomer?.contactPartner && !quoteCustomer?.contactCustomer) ?? false;
  }

  private setUpCreditCardScript(): void {
    if (this.isPaymentStepAvailable) {
      const script = document.createElement('script');
      const scriptHost =
        process.env.NODE_ENV === 'development' ? (process.env.REACT_APP_CC_HANDLE_SCRIPT as string) : 'https://paytokproxy.idt.net';
      script.src = scriptHost + '/idthandlers/jshandler/jsscript.ashx?req=cchandle';
      script.async = true;
      document.body.appendChild(script);
    }
  }

  get isEquipmentOrder() {
    const equipmentCategories = this._quoteInfo?.items?.some(x => EquipmentCategories.includes(x.lineitem?.category)) ?? false;
    return equipmentCategories || !!this.shippableItems?.length;
  }

  get shippableItems(): IDevice[] {
    return (
      this._quoteInfo?.items?.reduce((devices: IDevice[], x) => {
        if (x.lineitem.hardwaremfg && x.lineitem.hardwaresku) {
          devices.push({
            template: x.lineitem?.template?.toString(),
            qty: (x.qty ?? 0).toString(),
            make: x.lineitem.hardwaremfg,
            part: x.lineitem.hardwaresku,
          });
        }
        return devices;
      }, []) ?? []
    );
  }

  get hasAiItems() {
    return (
      this.quoteInfo?.items?.some(
        x => x.lineitem?.category == LineItemCategory.Net2phoneAi || x.lineitem?.category == LineItemCategory.Net2phoneCoach,
      ) ?? false
    );
  }

  get isAiOrder() {
    return (
      this.quoteInfo?.items?.every(
        x => x.lineitem?.category == LineItemCategory.Net2phoneAi || x.lineitem?.category == LineItemCategory.Net2phoneCoach,
      ) ?? false
    );
  }

  get hasNoAdditions() {
    return this.quoteInfo?.hasNoAdditions;
  }

  private getQuoteLanguage(customerLanguage: string | null): string {
    const language = customerLanguage?.toLowerCase();
    const tenantLanguage = this.root.tenantStore.config?.language ?? DefaultLanguage;
    const localePart = tenantLanguage.length > 2 ? tenantLanguage.substring(2) : '';
    return language && Languages.includes(language) ? `${language}${localePart}` : this.root.tenantStore.config?.language;
  }

  get isSelfOnboarding() {
    return this._quoteInfo?.onboardingType === OnboardingType.Self;
  }
}
